<script>
import { cloneDeep, isFunction } from '@/utils/lodashUtils';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export default {
  name: 'VirtualScroll',
  props: {
    itemHeight: {
      type: [Number, Function],
      required: true,
    },
    visibleCount: {
      type: [Number, Function],
      required: true,
    },
    cacheCount: {
      type: [Number, Function],
      default: 0, // equal to visibleCount if not set
    },
    itemTag: {
      type: String,
      default: 'div',
    },
    itemClass: {
      type: [String, Object],
      default: '',
    },
    wrapperTag: {
      type: String,
      default: 'div',
    },
    wrapperClass: {
      type: [String, Object],
      default: '',
    },
    spyScrollOn: {
      type: null,
      default: '',
    },
    startIndex: {
      type: [Number, Function],
      default: 0,
    },
    multipleMode: {
      type: Boolean,
      default: false,
    },
    instanceCount: {
      type: Number,
      default: 1,
    },
    instanceWidth: {
      type: Number,
      default: 0,
    },
    spyScrollXOn: {
      type: null,
      default: '',
    },
    hasYScroll: {
      type: Boolean,
      default: true,
    },
    hasXScroll: {
      type: Boolean,
      default: false,
    },
    debounce: {
      type: Number,
      default: 0,
    },
    item: {
      type: [Object, Function],
      default: null,
    },
    itemsCount: {
      type: [Number, Function],
      default: 0,
    },
    itemProps: {
      type: Function,
      default() {},
    },
    threshold: {
      type: Number,
      default: 0.1,
    },
  },
  data() {
    return {
      delta: [],

      scrollYObserver: null,
      scrollXObserver: null,
      resizeObserver: null,

      scrollLeft: 0,
      containerWidth: 0,
    };
  },
  computed: {
    visibleInstanceCount() {
      let count = Math.floor(this.containerWidth / (this.instanceWidth - 20)); // 20 - for scrollbar width
      const viewportRightBorder = this.scrollLeft + this.containerWidth;
      const visibleInstanceRightBorder =
        (count + this.firstVisibleInstance) * this.instanceWidth;
      if (
        viewportRightBorder - visibleInstanceRightBorder >
        this.instanceWidth * this.threshold
      ) {
        count++;
      }

      return Math.min(this.instanceCount, count);
    },
    firstVisibleInstance() {
      const index = Math.floor(
        (this.scrollLeft - this.instanceWidth * this.threshold) /
          this.instanceWidth
      );
      return Math.max(0, index);
    },
  },
  watch: {
    instanceCount() {
      this.initialize();
      this.forceRender();
    },
    visibleInstanceCount(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.initialize();
        this.forceRender();
      }
    },
    multipleMode() {
      this.initialize();
      this.forceRender();
    },
    visibleCount() {
      this.forceRender();
    },
    cacheCount() {
      this.forceRender();
    },
    startIndex() {
      this.initialize();
      this.forceRender();
    },
    itemsCount() {
      this.initialize();
      this.forceRender();
    },
  },
  created() {
    this.initialize();
    this.updateObserver = new Subject();
    this.updateObserver
      .pipe(debounceTime(this.debounce))
      .subscribe(this.forceRender);
  },
  mounted() {
    const debounce = debounceTime(this.debounce);

    if (this.hasYScroll && !this.scrollYObserver) {
      const $element = this.spyScrollOn
        ? document.body.querySelector(this.spyScrollOn)
        : null;

      this.scrollYObserver = fromEvent($element || window, 'scroll')
        .pipe(debounce)
        .subscribe(event => {
          this.onScroll(event, $element);
        });
    }
    if (this.hasXScroll && !this.scrollXObserver) {
      const $element = this.spyScrollXOn
        ? document.body.querySelector(this.spyScrollXOn)
        : null;
      this.scrollXObserver = fromEvent($element || window, 'scroll')
        .pipe(debounce)
        .subscribe(event => {
          this.onScrollX(event, $element);
        });
      this.containerWidth = $element.offsetWidth;
      this.resizeObserver = fromEvent(window, 'resize')
        .pipe(debounce)
        .subscribe(() => {
          this.containerWidth = $element.offsetWidth;
        });
    }
    if (!this.scrollObserver) {
      this.scrollObserver = new Subject();
      this.scrollObserver.pipe(debounce).subscribe(scrollData => {
        this.forceRender();
        this.$emit('scroll', scrollData);
      });
    }
  },
  beforeDestroy() {
    if (this.scrollYObserver) {
      this.scrollYObserver.unsubscribe();
    }
    if (this.scrollXObserver) {
      this.scrollXObserver.unsubscribe();
    }
    if (this.scrollObserver) {
      this.scrollObserver.unsubscribe();
    }
    this.updateObserver.unsubscribe();
  },
  methods: {
    initialize() {
      let delta;
      if (this.multipleMode) {
        delta = [];
        for (let i = 0; i < this.instanceCount; i += 1) {
          const itemHeight = this.getPropValue('itemHeight', i);
          const visibleCount = this.getPropValue('visibleCount', i);
          const startIndex = this.getPropValue('startIndex', i);
          const cacheCount = this.getPropValue('cacheCount', i);
          const itemsCount = this.getPropValue('itemsCount', i);
          const startPosition = startIndex >= visibleCount ? startIndex : 0;
          const keepInDOM = Math.min(
            visibleCount + (cacheCount || visibleCount),
            itemsCount
          );
          const totalHeight = itemHeight * itemsCount;

          delta[i] = {
            direction: '', // current scroll direction, D: down, U: up
            scrollTop: 0, // current scroll top, use to direction
            scrollLeft: 0, // current scroll left, use to direction
            start: startPosition, // start index
            end: Math.min(startPosition + keepInDOM - 1, itemsCount - 1), // end index
            keepInDOM, // nums keeping in real dom
            totalItems: itemsCount, // all items count, update in getItemsRender
            offsetAll: totalHeight - itemHeight * visibleCount || 0, // cache all the scrollable offset
            paddingTop: 0, // container wrapper real padding-top
            paddingBottom: 0, // container wrapper real padding-bottom
            isVisible: i < this.visibleInstanceCount,
          };
        }
      } else {
        // const itemHeight = this.getPropValue('itemHeight');
        const visibleCount = this.getPropValue('visibleCount');
        const startIndex = this.getPropValue('startIndex');
        const cacheCount = this.getPropValue('cacheCount');
        const itemsCount = this.getPropValue('itemsCount');
        const startPosition = startIndex >= visibleCount ? startIndex : 0;

        const keepInDOM = visibleCount + (cacheCount || visibleCount);

        delta = [
          {
            direction: '', // current scroll direction, D: down, U: up
            scrollTop: 0, // current scroll top, use to direction
            scrollLeft: 0, // current scroll left, use to direction
            start: startPosition, // start index
            end: Math.min(startPosition + keepInDOM - 1, itemsCount - 1), // end index
            keepInDOM, // nums keeping in real dom
            totalItems: itemsCount, // all items count, update in getItemsRender
            offsetAll: 0, // cache all the scrollable offset
            paddingTop: 0, // container wrapper real padding-top
            paddingBottom: 0, // container wrapper real padding-bottom
          },
        ];
      }
      this.delta = delta;
    },
    getPropValue(prop = '', instanceIndex = 0) {
      return !prop
        ? null
        : isFunction(this[prop])
        ? this[prop](instanceIndex)
        : this[prop];
    },
    getDelta(instanceIndex = 0) {
      return cloneDeep(this.delta[instanceIndex]);
    },
    onScroll(event, element) {
      let offsetAll, reachBottom, reachTop;
      const scrollContent = element || window;
      let { top, bottom } = this.$el.getBoundingClientRect();

      if (element) {
        const elementRect = element.getBoundingClientRect();
        top -= elementRect.top;
        bottom -= elementRect.top;
      }

      if (bottom < 0 || top > scrollContent.outerHeight) {
        return;
      }

      const offset = Math.max(-top, 0) || 0;

      if (this.multipleMode) {
        for (let i = 0; i < this.instanceCount; i += 1) {
          const delta = this.getDelta(i);

          if (delta) {
            delta.direction = offset > delta.scrollTop ? 'D' : 'U';
            delta.scrollTop = offset;
            if (delta.totalItems > delta.keepInDOM) {
              this.updateZone({ offset, instanceIndex: i, delta });
            } else {
              delta.end = delta.totalItems > 1 ? delta.totalItems - 1 : 1;
              this.delta[i] = delta;
            }
          }
        }
        offsetAll = this.delta[0].offsetAll;
        reachTop = !offset && this.delta[0].totalItems;
        reachBottom = offset >= offsetAll;
      } else {
        const delta = this.getDelta();
        delta.direction = offset > delta.scrollTop ? 'D' : 'U';
        delta.scrollTop = offset;
        if (delta.totalItems > delta.keepInDOM) {
          this.updateZone({ offset, delta });
        } else {
          delta.end = delta.totalItems - 1;
        }

        offsetAll = delta.offsetAll;
        reachTop = !offset && delta.totalItems;
        reachBottom = offset >= offsetAll;
        this.delta[0] = delta;
      }

      this.scrollObserver.next({
        offsetY: offset,
        offsetAll,
        originalEvent: event,
      });
      if (reachTop) {
        this.$emit('reachTop');
      }

      if (reachBottom) {
        this.$emit('reachBottom');
      }

      this.$nextTick(() => this.emitVisibleData());
    },
    onScrollX(event, element) {
      const firstDelta = this.getDelta(0);
      const offsetY = firstDelta.scrollTop;
      let { left } = this.$el.getBoundingClientRect();

      if (element) {
        left -= element.getBoundingClientRect().left;
      }

      const offsetX =
        Math.max(
          -left, // scrollContent.scrollLeft - (scrollContent.scrollLeft + $el.clientX)
          0
        ) || 0;

      const offsetAll = firstDelta.offsetAll;
      const offsetXAll = element
        ? element.scrollWidth - element.clientWidth
        : document.body.scrollWidth - document.body.clientWidth;

      // todo: we must consider deviations from 0 to 1px
      const reachLeft = offsetX <= 1;
      const reachRight = offsetX >= offsetXAll - 1;

      this.scrollLeft = offsetX;

      if (this.multipleMode) {
        if (this.instanceWidth) {
          const firstVisibleInstance = Math.floor(offsetX / this.instanceWidth);

          for (let i = 0; i < this.instanceCount; i += 1) {
            const delta = this.getDelta(i);
            if (delta) {
              delta.directionX = offsetX > delta.scrollLeft ? 'R' : 'L';
              delta.scrollLeft = offsetX;
              delta.offsetXAll = offsetXAll;
              delta.isVisible =
                firstVisibleInstance + this.visibleInstanceCount > i &&
                i >= firstVisibleInstance;
              if (this.instanceCount > this.visibleInstanceCount) {
                this.updateZone({
                  offset: delta.scrollTop,
                  offsetLeft: offsetX,
                  instanceIndex: i,
                  delta,
                });
              }
            }
          }
        }
      }

      this.scrollObserver.next({
        offsetY,
        offsetX,
        offsetAll,
        offsetXAll,
        originalEvent: event,
      });
      if (reachLeft) {
        this.$emit('reachLeft');
      }

      if (reachRight) {
        this.$emit('reachRight');
      }

      this.$nextTick(() => this.emitVisibleData());
    },
    updateZone({ offset, offsetLeft, delta, instanceIndex = 0 } = {}) {
      const itemHeight = this.getPropValue('itemHeight', instanceIndex);
      let scrolledFromTopCount = itemHeight
        ? Math.floor(offset / itemHeight)
        : 0;

      const zone = this.getZone(scrolledFromTopCount, delta);

      const isSameZoneVertical =
        zone.start === delta.start && zone.end === delta.end;
      const isSameZoneHorizontal =
        this.delta[instanceIndex].scrollLeft === offsetLeft;
      if (!isSameZoneVertical || !isSameZoneHorizontal) {
        delta.end = zone.end;
        delta.start = zone.start;
        this.delta[instanceIndex] = delta;

        this.updateObserver.next(true);
      }
    },
    // return the right zone info base on `start/index`.
    getZone(startIndex, delta) {
      let start, end;
      const index = Math.max(0, parseInt(startIndex, 10));

      const lastPossibleStartIndex = delta.totalItems - delta.keepInDOM;
      const isLast =
        (delta.totalItems >= index && index >= lastPossibleStartIndex) ||
        index > delta.totalItems;

      if (isLast) {
        end = delta.totalItems - 1;
        start = Math.max(0, lastPossibleStartIndex);
      } else {
        start = index;
        end = start + delta.keepInDOM - 1;
      }

      return {
        end,
        start,
        isLast,
      };
    },
    // public method, force render ui list if we needed.
    // call this before the next repaint to get better performance.
    forceRender() {
      window.requestAnimationFrame(() => {
        this.$forceUpdate();
      });
    },
    getItemsRender(h, instanceIndex = 0) {
      const delta = this.getDelta(instanceIndex);
      const itemsCount = this.getPropValue('itemsCount', instanceIndex);
      const itemHeight = this.getPropValue('itemHeight', instanceIndex);
      const item = this.getPropValue('item', instanceIndex);
      // item-mode should judge from items prop.
      if (item) {
        delta.totalItems = itemsCount;
        if (delta.keepInDOM > delta.totalItems) {
          delta.end = delta.totalItems > 1 ? delta.totalItems - 1 : 1;
        }
      }

      const hasPadding = delta.totalItems > delta.keepInDOM;
      const paddingTop = itemHeight * (hasPadding ? delta.start : 0);

      const paddingBottom =
        itemHeight * (hasPadding ? delta.totalItems - delta.keepInDOM : 0) -
        paddingTop;

      delta.paddingTop = paddingTop;
      delta.paddingBottom = paddingBottom < itemHeight ? 0 : paddingBottom;

      let renders = [];
      for (
        let i = delta.start;
        i < delta.totalItems && i <= Math.ceil(delta.end);
        i += 1
      ) {
        let slot = h(item, {
          ...this.itemProps(i, instanceIndex),
          style: {
            height: itemHeight < 0 ? 'auto' : `${itemHeight}px`,
          },
        });
        renders.push(slot);
      }
      this.delta[instanceIndex] = delta;

      return renders;
    },

    renderItem(h, { paddingTop, paddingBottom, list }) {
      return h(
        this.itemTag,
        {
          class: this.itemClass,
          style: {
            'padding-top': paddingTop + 'px',
            'padding-bottom': paddingBottom + 'px',
          },
          attrs: {
            role: 'group',
          },
        },
        list
      );
    },

    emitVisibleData() {
      const { start, end } = this.getDelta();
      this.$emit('visibleChange', {
        firstVisibleInstance: this.firstVisibleInstance,
        visibleInstanceCount: this.visibleInstanceCount,
        firstVisibleItem: start,
        lastVisibleItem: end,
      });
    },
  },

  render(h) {
    let render;

    if (this.multipleMode) {
      if (this.visibleInstanceCount && this.instanceWidth) {
        let paddingLeft, paddingRight;
        const hasHorizontalPadding =
          this.instanceCount > this.visibleInstanceCount;

        const renderItems = [];
        const lastInstanceIndex = Math.min(
          this.firstVisibleInstance + this.visibleInstanceCount,
          this.instanceCount
        );

        for (let i = this.firstVisibleInstance; i < lastInstanceIndex; i += 1) {
          const delta = this.getDelta(i);
          if (delta) {
            const list = this.getItemsRender(h, i);
            const { paddingTop, paddingBottom } = delta;

            renderItems.push(
              this.renderItem(h, {
                paddingTop,
                paddingBottom,
                list,
              })
            );
          }
        }

        paddingLeft =
          this.instanceWidth *
          (hasHorizontalPadding ? this.firstVisibleInstance : 0);
        paddingRight =
          this.instanceWidth *
            (hasHorizontalPadding
              ? this.instanceCount - this.visibleInstanceCount
              : 0) -
          paddingLeft;
        render = h(
          this.wrapperTag,
          {
            class: this.wrapperClass,
            attrs: {
              role: 'group',
            },
            style: {
              padding: `0 ${paddingRight}px 0 ${paddingLeft}px`,
              minWidth:
                -1 + //fix for unnecessary initial horizontal scroll
                paddingLeft +
                paddingRight +
                this.instanceWidth * this.visibleInstanceCount +
                'px',
            },
            key: `vsl_wrapper_${this._uid}`,
          },
          renderItems
        );
      }
    } else {
      const list = this.getItemsRender(h);
      const { paddingTop, paddingBottom } = this.delta[0];
      render = this.renderItem(h, { paddingTop, paddingBottom, list });
    }

    return render;
  },
};
</script>
